/**
 * Strategy Validators
 * 
 * Validation utilities for strategy form data.
 * Extracted from StrategyWizardDialog.tsx for better maintainability.
 */

import { StrategyFormData, Leg, ValidationResult } from '../../types/strategy';
import {
  UNDERLYING,
  MONTHLY_ONLY_UNDERLYINGS,
  EXPIRY_TYPE_CONST,
  TRADING_TYPE,
  WEEKDAYS,
  ENTRY_SELECTION_MODE,
  INTRADAY_DTE_OPTIONS,
  MAX_MAIN_LEGS,
  MAX_HEDGE_LEGS,
  UL_RISK_TYPES,
  UL_TSL_TYPES,
  UL_WAT_TYPES,
  RE_ENTRY_TYPE,
  WAT_TYPE,
} from '../../constants/tradingConstants';

import { validatePercentageOfUnderlying } from './methodValidators';
import { METHOD_OWNED_FIELDS, mainRecycleEligible } from './positionHelpers';

// Percentage-family W&T types (premium PERCENTAGE + both UL_PCT directions).
// The SELL ≥100% fat-finger guard fires on any of these — UL_PCT_UP/DOWN
// were missed pre-Phase 6 review-fix H6 and would accept typos like 200 for 2%.
const PERCENT_WAT_TYPES: readonly string[] = [
  WAT_TYPE.PERCENTAGE,
  WAT_TYPE.UL_PCT_UP,
  WAT_TYPE.UL_PCT_DOWN,
];

const VALID_UNDERLYINGS = Object.values(UNDERLYING) as string[];

// Risk features a hedge leg may NOT carry — mirrors the backend
// _HEDGE_FORBIDDEN_FEATURES in libs/shared/shared/validators.py.
const HEDGE_FORBIDDEN_FEATURES: { key: keyof Leg; label: string }[] = [
  { key: 'stopLoss', label: 'Stop Loss' },
  { key: 'targetProfit', label: 'Target Profit' },
  { key: 'trailingStopLoss', label: 'Trailing Stop Loss' },
  { key: 'waitAndTrade', label: 'Wait & Trade' },
  { key: 'reEntry', label: 'Re-Entry' },
  { key: 'reExecute', label: 'Re-Execute' },
];

/**
 * Validate strategy name
 */
export const validateStrategyName = (name: string): string[] => {
  const errors: string[] = [];
  
  if (!name?.trim()) {
    errors.push('Strategy name is required');
  }
  
  if (name && name.trim().length < 3) {
    errors.push('Strategy name must be at least 3 characters long');
  }
  
  if (name && name.trim().length > 100) {
    errors.push('Strategy name must be less than 100 characters');
  }
  
  return errors;
};

/**
 * Validate positions/legs
 */
export const validatePositions = (legs: Leg[]): string[] => {
  const errors: string[] = [];

  if (!legs || legs.length === 0) {
    errors.push('At least one position is required');
    return errors;
  }

  // Main and hedge legs have independent caps (mirrors the backend).
  const mainCount = legs.filter((l) => !l.isHedge).length;
  const hedgeCount = legs.length - mainCount;
  if (mainCount === 0) {
    errors.push('At least one main leg is required');
  }
  if (mainCount > MAX_MAIN_LEGS) {
    errors.push(`Strategy supports at most ${MAX_MAIN_LEGS} main legs (you have ${mainCount})`);
  }
  if (hedgeCount > MAX_HEDGE_LEGS) {
    errors.push(`Strategy supports at most ${MAX_HEDGE_LEGS} hedge legs (you have ${hedgeCount})`);
  }

  legs.forEach((leg, index) => {
    const positionErrors = validateSinglePosition(leg, index + 1);
    errors.push(...positionErrors);
  });

  errors.push(...validateHedgeLinks(legs));

  return errors;
};

/**
 * Cross-leg hedge linkage checks — mirrors the backend validate_hedge_legs /
 * resolve_hedge_links contract. A hedge must reference an existing MAIN leg
 * (not itself, not another hedge), and a main leg can carry at most one hedge.
 */
export const validateHedgeLinks = (legs: Leg[]): string[] => {
  const errors: string[] = [];
  const claimedMains = new Set<string>();

  legs.forEach((leg, index) => {
    if (!leg.isHedge) return;
    const prefix = `Position ${index + 1}:`;
    const target = legs.find((l) => l.id === leg.hedgeForLegId);
    if (!leg.hedgeForLegId || !target || target.isHedge || target.id === leg.id) {
      errors.push(`${prefix} A hedge leg must reference an existing main leg`);
      return;
    }
    // A hedge protects a short — it is only valid on a SELL main leg.
    if (target.actionType !== 'SELL') {
      errors.push(`${prefix} A hedge leg can only protect a SELL main leg`);
    }
    // A recycle-opted hedge requires its main to have an early-exit-or-re-entry trigger
    // (SL / TSL / TP / re-entry / re-execute) — mirrors backend validate_hedge_legs. With
    // none of these the main only exits at strategy-exit, where the base main-first ordering
    // already releases the hedge, so recycle would add nothing.
    if (leg.hedgeRecycle && !mainRecycleEligible(target)) {
      errors.push(`${prefix} A recycle-enabled hedge requires its main leg to have a stop-loss, trailing-stop-loss, target-profit, re-entry, or re-execute enabled`);
    }
    // A hedge must carry the SAME lots as its main leg (equal lots keep the
    // freeze-split chunks aligned 1:1, which the recycle relies on). Mirror of
    // validate_hedge_legs. (The builder also locks the hedge lot field to its main.)
    if (leg.totalLots > 0 && target.totalLots > 0 && leg.totalLots !== target.totalLots) {
      errors.push(`${prefix} A hedge leg must have the same lots as its main leg`);
    }
    if (claimedMains.has(target.id)) {
      errors.push(`${prefix} A main leg can have at most one hedge leg`);
    }
    claimedMains.add(target.id);
  });

  return errors;
};

/**
 * Validate a single position
 */
export const validateSinglePosition = (leg: Leg, positionNumber: number): string[] => {
  const errors: string[] = [];
  const prefix = `Position ${positionNumber}:`;

  // Validate lots
  if (leg.totalLots <= 0) {
    errors.push(`${prefix} Total lots must be greater than 0`);
  }

  if (leg.totalLots > 200) {
    errors.push(`${prefix} Total lots cannot exceed 200`);
  }

  // Validate strike (selection_value): required for ATM_POINTS / ATM_PERCENT methods.
  // PREMIUM, PERCENTAGE_OF_STRADDLE_PREMIUM, and PERCENTAGE_OF_UNDERLYING don't
  // carry a strike string — they use premiumValue / straddlePremiumPercentage /
  // underlyingPercentage instead, validated below.
  if (
    (leg.selectionMethod === 'ATM_POINTS' || leg.selectionMethod === 'ATM_PERCENT') &&
    !leg.strikePrice?.toString().trim()
  ) {
    errors.push(`${prefix} Strike selection is required`);
  }

  // Validate premium criteria for PREMIUM method
  if (leg.selectionMethod === 'PREMIUM') {
    if (!leg.premiumOperator) {
      errors.push(`${prefix} Premium operator is required for Premium selection`);
    }
    
    if (leg.premiumValue === undefined || leg.premiumValue < 0) {
      errors.push(`${prefix} Premium value must be a positive number for Premium selection`);
    }
  }
  
  // Validate straddle premium criteria for PERCENTAGE_OF_STRADDLE_PREMIUM method
  if (leg.selectionMethod === 'PERCENTAGE_OF_STRADDLE_PREMIUM') {
    if (!leg.straddlePremiumOperator) {
      errors.push(`${prefix} Straddle premium operator is required for Percentage of Straddle Premium selection`);
    }

    if (leg.straddlePremiumPercentage === undefined || leg.straddlePremiumPercentage < 5 || leg.straddlePremiumPercentage > 60) {
      errors.push(`${prefix} Straddle premium percentage must be between 5% and 60%`);
    }
  }

  // Validate straddle width multiplier for STRADDLE_WIDTH method
  if (leg.selectionMethod === 'STRADDLE_WIDTH') {
    if (leg.straddleWidth === undefined || leg.straddleWidth < -10 || leg.straddleWidth > 10) {
      errors.push(`${prefix} Straddle width must be between -10 and +10`);
    }
  }

  // Validate percent + operator for PERCENTAGE_OF_UNDERLYING method
  if (leg.selectionMethod === 'PERCENTAGE_OF_UNDERLYING') {
    errors.push(...validatePercentageOfUnderlying(leg, prefix));
  }
  
  // Associate-hedge per-leg rules (mirrors backend validate_hedge_legs): a hedge
  // is BUY-only and carries NO risk features. Main legs validate risk normally;
  // hedges forbid it, so we don't also run the risk-value checks on them.
  if (leg.isHedge) {
    if (leg.actionType !== 'BUY') {
      errors.push(`${prefix} A hedge leg must be a BUY leg`);
    }
    for (const { key, label } of HEDGE_FORBIDDEN_FEATURES) {
      const feature = leg[key] as { enabled?: boolean } | undefined;
      if (feature?.enabled) {
        errors.push(`${prefix} A hedge leg cannot have ${label} enabled`);
      }
    }
  } else {
    const riskErrors = validateRiskManagement(leg, positionNumber);
    errors.push(...riskErrors);
  }

  return errors;
};

/**
 * Validate risk management configuration for a position
 */
export const validateRiskManagement = (leg: Leg, positionNumber: number): string[] => {
  const errors: string[] = [];
  const prefix = `Position ${positionNumber}:`;

  // Validate stop loss (with defensive checks)
  // RANGE type uses rangeSide (HIGH/LOW) instead of a numeric value
  if (leg.stopLoss?.enabled && leg.stopLoss?.type !== 'RANGE' && (leg.stopLoss?.value || 0) <= 0) {
    errors.push(`${prefix} Stop loss value must be greater than 0 when enabled`);
  }

  // Validate target profit (with defensive checks)
  if (leg.targetProfit?.enabled && (leg.targetProfit?.value || 0) <= 0) {
    errors.push(`${prefix} Target profit value must be greater than 0 when enabled`);
  }
  
  // Validate trailing stop loss (with defensive checks)
  if (leg.trailingStopLoss?.enabled) {
    if (!leg.stopLoss?.enabled) {
      errors.push(`${prefix} Trailing stop loss requires stop loss to be enabled`);
    }

    if ((leg.trailingStopLoss?.instrumentMoveValue || 0) <= 0) {
      errors.push(`${prefix} Instrument move value must be greater than 0 for trailing stop loss`);
    }

    if ((leg.trailingStopLoss?.stopLossMoveValue || 0) <= 0) {
      errors.push(`${prefix} Stop loss move value must be greater than 0 for trailing stop loss`);
    }
  }

  // Validate wait and trade (with defensive checks)
  if (leg.waitAndTrade?.enabled && (leg.waitAndTrade?.value || 0) <= 0) {
    errors.push(`${prefix} Wait & trade value must be greater than 0 when enabled`);
  }
  // Sell-side WAT with any percentage-family type (premium or UL) waits for a
  // percentage move before entering — 100%+ typically means a fat-finger typo
  // (e.g. 200 for 20). Cap at 99% to catch the common mistake without blocking
  // legitimate "wait for premium/spot to double" cases (which can use POINTS
  // types instead). Widened for UL_PCT_UP / UL_PCT_DOWN per Phase 6 review H6.
  // UL % types carry an explicit direction, so 100%+ is a typo on either side;
  // mirrors backend validate_wat_config.
  const isUlPctWat = leg.waitAndTrade?.type === WAT_TYPE.UL_PCT_UP
    || leg.waitAndTrade?.type === WAT_TYPE.UL_PCT_DOWN;
  if (leg.waitAndTrade?.enabled
      && PERCENT_WAT_TYPES.includes(leg.waitAndTrade.type)
      && (leg.actionType === 'SELL' || isUlPctWat)
      && (leg.waitAndTrade?.value || 0) >= 100) {
    errors.push(isUlPctWat
      ? `${prefix} UL Wait & trade percentage must be less than 100`
      : `${prefix} Sell-side Wait & trade percentage must be less than 100`);
  }
  
  // Validate re-entry count (with defensive checks)
  if (leg.reEntry?.enabled && ((leg.reEntry?.count || 0) < 1 || (leg.reEntry?.count || 0) > 5)) {
    errors.push(`${prefix} Re-entry count must be between 1 and 5`);
  }

  // Validate re-execute count (with defensive checks)
  if (leg.reExecute?.enabled && ((leg.reExecute?.count || 0) < 1 || (leg.reExecute?.count || 0) > 5)) {
    errors.push(`${prefix} Re-execute count must be between 1 and 5`);
  }

  // Phase 6 — UL cross-config mirror of backend _validate_ul_cross_config.
  // Best-effort client-side gate; the server is still the source of truth
  // (it also gates on UL_MONITOR_ENABLED which the frontend doesn't know).
  errors.push(...validateUlCrossConfig(leg, prefix));

  return errors;
};

/**
 * UL cross-config rules — mirrors backend `_validate_ul_cross_config`.
 * UL SL / TP / TSL work with any entry mode (immediate, premium W&T, ORB,
 * UL W&T). Runs after the per-feature value-range checks so we don't
 * double-report on "value must be > 0" etc.
 *
 * Rules:
 * 1. M7 — UL TSL requires UL SL on the same leg. TSL ratchets
 *    `underlying_sl_level`; without a UL SL level there's nothing to ratchet.
 * 2. A premium TSL on a UL SL never trails — no broker stop rests to move.
 * 3. Phase 5j.MEDIUM — on a UL W&T leg with `trade_only_first_entry=false`,
 *    SL_REEXEC and re-execute on target (fresh strike at market) can't re-run
 *    the UL wait and are rejected, each with its own remedy. SL_REENTRY and
 *    SL_RECOST (any SL) re-wait.
 */
export const validateUlCrossConfig = (leg: Leg, prefix: string): string[] => {
  const errors: string[] = [];

  const hasUlSl  = !!(leg.stopLoss?.enabled && UL_RISK_TYPES.includes(leg.stopLoss.type));
  const hasUlTsl = !!(leg.trailingStopLoss?.enabled && UL_TSL_TYPES.includes(leg.trailingStopLoss.type));
  const hasUlWat = !!(leg.waitAndTrade?.enabled && UL_WAT_TYPES.includes(leg.waitAndTrade.type));

  // M7 — UL TSL requires UL SL.
  if (hasUlTsl && !hasUlSl) {
    errors.push(
      `${prefix} UL trailing-stop-loss requires a UL stop-loss on the same leg`,
    );
  }

  // Premium TSL on a UL SL never trails — no broker stop rests to move.
  if (hasUlSl && leg.trailingStopLoss?.enabled && !hasUlTsl) {
    errors.push(
      `${prefix} A trailing SL on a UL stop-loss must use a UL type (TSL UL Pt or TSL UL %) — a premium trailing SL has nothing to move on a UL leg`,
    );
  }

  // Phase 5j.MEDIUM — children that re-run the full entry at market can't
  // re-run the UL W&T wait. Each violation is reported with its own remedy.
  if (hasUlWat && !leg.waitAndTrade?.tradeOnlyFirstEntry) {
    const noRewait: { what: string; fix: string }[] = [];
    if (leg.reEntry?.enabled && leg.reEntry.type === RE_ENTRY_TYPE.SL_REEXEC) {
      noRewait.push({ what: 'SL_REEXEC re-entry', fix: 'Use SL_REENTRY or SL_RECOST for per-child re-wait.' });
    }
    if (leg.reExecute?.enabled) {
      noRewait.push({ what: 'Re-execute on target', fix: 'Otherwise turn off Re-execute.' });
    }
    noRewait.forEach(({ what, fix }) => errors.push(
      `${prefix} ${what} on a UL wait-and-trade leg requires "First entry only" — it re-runs the full entry flow at current market and cannot re-run the UL W&T wait. ${fix}`,
    ));
  }

  return errors;
};

/**
 * Validate time configuration
 */
export const validateTimeConfiguration = (config: any): string[] => {
  const errors: string[] = [];
  
  const entryHour = parseInt(config.entryTimeHour);
  const exitHour = parseInt(config.exitTimeHour);
  const entryMinute = parseInt(config.entryTimeMinute);
  const exitMinute = parseInt(config.exitTimeMinute);
  
  // Validate hour ranges
  if (entryHour < 9 || entryHour > 15) {
    errors.push('Entry time must be between 09:16 and 15:39');
  }

  if (exitHour < 9 || exitHour > 15) {
    errors.push('Exit time must be between 09:16 and 15:39');
  }
  
  // Validate that entry time is before exit time (with BTST support)
  const entryTimeMinutes = entryHour * 60 + entryMinute;
  const exitTimeMinutes = exitHour * 60 + exitMinute;

  // POSITIONAL trading days validation
  if (config.tradingType === 'POSITIONAL') {
    const entryDays = config.entryTradingDaysBeforeExpiry;
    const exitDays = config.exitTradingDaysBeforeExpiry;

    if (entryDays < exitDays) {
      errors.push('Entry trading days must be greater than or equal to exit trading days for positional strategies');
    }

    // Same-day POSITIONAL trades need intraday time validation
    if (entryDays === exitDays && entryTimeMinutes >= exitTimeMinutes) {
      errors.push('Entry time must be before exit time for same-day positional strategies');
    }
  } else {
    // INTRADAY time validation with proper BTST support
    const isNextDayExit = config.tradingType === 'INTRADAY' && config.intradayExitMode === 'NEXT_DAY_BTST';

    if (!isNextDayExit && entryTimeMinutes >= exitTimeMinutes) {
      errors.push('Entry time must be before exit time');
    }

    // Validate range breakout time if enabled (only for INTRADAY)
    if (config.rangeBreakout) {
      const rangeHour = parseInt(config.rangeBreakoutTimeHour);
      const rangeMinute = parseInt(config.rangeBreakoutTimeMinute);
      const rangeTimeMinutes = rangeHour * 60 + rangeMinute;

      if (rangeTimeMinutes <= entryTimeMinutes) {
        errors.push('Range breakout time must be after entry time');
      }

      // For SAME_DAY exit, exit time must be after range breakout time (since entry happens at breakout)
      if (!isNextDayExit && exitTimeMinutes <= rangeTimeMinutes) {
        errors.push('Exit time must be after range breakout time for same-day strategies');
      }
    }
  }

  // Mirror backend validation: expiry_day_rollover requires INTRADAY+NEXT_DAY_BTST.
  // Applies for all tradingType / intradayExitMode combinations so a rollover-true
  // flag can never be persisted against a combo where the entry path silently
  // ignores it.
  const isBtstCombo =
    config.tradingType === 'INTRADAY' && config.intradayExitMode === 'NEXT_DAY_BTST';
  if (config.expiryDayRollover && !isBtstCombo) {
    errors.push('Continue on expiry days requires Next Day (BTST/STBT) exit mode');
  }

  // next_weekly / next_monthly + expiry_day_rollover=True is valid.
  // Chain-lock rollover is handled at execution time by the backend when
  // the running chain reaches the locked contract's own expiry day.

  return errors;
};

/**
 * Validate basket ID
 */
export const validateBasketId = (basketId: string): string[] => {
  const errors: string[] = [];

  if (!basketId?.trim()) {
    errors.push('Basket ID is required');
  }

  return errors;
};

/**
 * Validate strategy-level underlying (index).
 * Backend rejects payloads without an underlying or with one outside the whitelist
 * (strategy_manager.py:291-304).
 */
export const validateUnderlying = (index: string): string[] => {
  const errors: string[] = [];

  if (!index?.trim()) {
    errors.push('Underlying selection is required');
    return errors;
  }

  if (!VALID_UNDERLYINGS.includes(index)) {
    errors.push(`Underlying must be one of: ${VALID_UNDERLYINGS.join(', ')}`);
  }

  return errors;
};

/**
 * Validate underlying ↔ expiry-type compatibility.
 * BANKNIFTY, FINNIFTY, MIDCPNIFTY and BANKEX only have monthly contracts —
 * backend rejects them with weekly expiry (strategy_manager.py:307-320).
 */
export const validateUnderlyingExpiryCompat = (index: string, expiryType: string): string[] => {
  const errors: string[] = [];

  if (
    index &&
    MONTHLY_ONLY_UNDERLYINGS.includes(index) &&
    expiryType !== EXPIRY_TYPE_CONST.MONTHLY &&
    expiryType !== EXPIRY_TYPE_CONST.NEXT_MONTHLY
  ) {
    errors.push(`${index} only supports monthly / next monthly expiry`);
  }

  return errors;
};

/**
 * Validate INTRADAY entry-selection fields.
 *
 * Branches by entrySelectionMode:
 *   WEEKDAY (default): require non-empty entryDays, subset of MON-FRI.
 *   DTE: require non-empty entryDtes, each in INTRADAY_DTE_OPTIONS.
 *
 * Exit-side stays weekday-only (DTE mode is entry-only in v1). POSITIONAL
 * strategies don't use these fields, so we only check on INTRADAY. Mirrors
 * backend `_validate_schedule_fields` in strategy_manager.py.
 */
export const validateTradingDays = (config: any): string[] => {
  const errors: string[] = [];

  if (config?.tradingType !== TRADING_TYPE.INTRADAY) return errors;

  const entryDays: string[] = Array.isArray(config?.entryDays) ? config.entryDays : [];
  const exitDays: string[] = Array.isArray(config?.exitDays) ? config.exitDays : [];
  const selectionMode = config?.entrySelectionMode === ENTRY_SELECTION_MODE.DTE
    ? ENTRY_SELECTION_MODE.DTE
    : ENTRY_SELECTION_MODE.WEEKDAY;

  if (selectionMode === ENTRY_SELECTION_MODE.DTE) {
    const entryDtes: number[] = Array.isArray(config?.entryDtes) ? config.entryDtes : [];
    if (entryDtes.length === 0) {
      errors.push('Pick at least one DTE');
    } else {
      const invalid = entryDtes.filter((d) => !INTRADAY_DTE_OPTIONS.includes(Number(d)));
      if (invalid.length > 0) {
        errors.push(`Entry DTE contains invalid value(s): ${invalid.join(', ')}`);
      }
    }
  } else {
    // WEEKDAY mode
    if (entryDays.length === 0) {
      errors.push('Entry days must include at least one weekday');
    } else {
      const invalid = entryDays.filter((d) => !WEEKDAYS.includes(d));
      if (invalid.length > 0) {
        errors.push(`Entry days contain invalid value(s): ${invalid.join(', ')}`);
      }
    }
  }

  if (exitDays.length === 0) {
    errors.push('Exit days must include at least one weekday');
  } else {
    const invalid = exitDays.filter((d) => !WEEKDAYS.includes(d));
    if (invalid.length > 0) {
      errors.push(`Exit days contain invalid value(s): ${invalid.join(', ')}`);
    }
  }

  return errors;
};

/**
 * Main validation function for complete strategy form data
 */
export const validateStrategyForm = (data: StrategyFormData): ValidationResult => {
  const errors: string[] = [];

  // Validate basket ID
  errors.push(...validateBasketId(data.basketId));

  // Validate strategy name
  errors.push(...validateStrategyName(data.strategyName));

  // Validate underlying (index) and its compatibility with the chosen expiry type
  errors.push(...validateUnderlying(data.index));
  errors.push(...validateUnderlyingExpiryCompat(data.index, data.config?.expiryType));

  // Validate INTRADAY weekday filters
  errors.push(...validateTradingDays(data.config));

  // Validate positions
  errors.push(...validatePositions(data.legs));

  // Validate time configuration
  errors.push(...validateTimeConfiguration(data.config));

  errors.push(...validateRangeBreakoutUlConflict(data.config, data.legs));

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Range breakout (ORB) fires every leg on the breakout and ignores a per-leg
 * UL wait-and-trade — mirrors backend `_validate_range_breakout_ul_conflict`.
 * UL SL / TP / TSL with ORB are fine.
 */
export const validateRangeBreakoutUlConflict = (config: any, legs: Leg[]): string[] => {
  if (!config?.rangeBreakout) return [];
  const index = (legs || []).findIndex(
    (leg) => leg.waitAndTrade?.enabled && UL_WAT_TYPES.includes(leg.waitAndTrade.type),
  );
  return index === -1
    ? []
    : [`Position ${index + 1}: Range breakout and UL wait-and-trade can't be combined — turn off range breakout or use a premium wait-and-trade`];
};

/**
 * Quick validation for required fields only
 */
export const validateRequiredFields = (data: Partial<StrategyFormData>): ValidationResult => {
  const errors: string[] = [];

  if (!data.strategyName?.trim()) {
    errors.push('Please enter a strategy name');
  }

  if (!data.legs || data.legs.length === 0) {
    errors.push('Please add at least one position');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * A set of legs that share the same "leg type" (option type + action), the same
 * strike-selection method, AND the same selection value (the method-owned value
 * field(s) — e.g. ATM points, premium value/operator, straddle width). Two such
 * legs are a fully-redundant/duplicate configuration, so we surface a soft
 * confirmation (not a hard block) before save.
 */
export interface DuplicateLegTypeGroup {
  optionType: string;      // CE | PE
  actionType: string;      // BUY | SELL
  selectionMethod: string; // SelectionMethod value
  /** Human-readable selection value(s) the group shares, e.g. "CLOSEST 100" or "ATM". */
  valueLabel: string;
  /** 1-based positions of the legs in this group, in leg order. */
  legNumbers: number[];
}

/**
 * Find groups of legs identical across leg type (optionType + actionType),
 * strike-selection method, and the selection value(s) that method owns
 * (per METHOD_OWNED_FIELDS — so a PREMIUM leg keys on operator + value, an
 * ATM leg on its strike points, etc.). Returns one entry per colliding group
 * (size > 1); an empty array means no duplicates. Pure — no side effects.
 */
export const findDuplicateLegTypeGroups = (legs: Leg[]): DuplicateLegTypeGroup[] => {
  const byKey = new Map<string, DuplicateLegTypeGroup>();

  (legs || []).forEach((leg, index) => {
    const ownedFields = METHOD_OWNED_FIELDS[leg.selectionMethod] ?? [];
    const values = ownedFields.map((f) => String(leg[f] ?? ''));
    const key = `${leg.optionType}|${leg.actionType}|${leg.selectionMethod}|${values.join('~')}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        optionType: leg.optionType,
        actionType: leg.actionType,
        selectionMethod: leg.selectionMethod,
        valueLabel: values.filter((v) => v !== '').join(' '),
        legNumbers: [],
      };
      byKey.set(key, group);
    }
    group.legNumbers.push(index + 1);
  });

  // Map preserves first-seen insertion order, so no separate order array is needed.
  return Array.from(byKey.values()).filter((group) => group.legNumbers.length > 1);
};